import React from 'react';
import { InterfaceType, SceneTurn } from '../../types';
import styles from './ExampleScene.module.css';

interface ExampleSceneProps {
  turns: SceneTurn[];
  interfaceType: InterfaceType;
}

const frameLabel: Record<InterfaceType, string> = {
  'Chat UI': 'Chat',
  'Voice UI': 'Voice',
  'Agentic UI': 'Agent activity',
};

const MicIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 10a7 7 0 0 0 14 0M12 17v5" />
  </svg>
);

const SpeakerIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5 6 9H2v6h4l5 4V5z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14" />
  </svg>
);

function Actions({ actions }: { actions?: string[] }) {
  if (!actions?.length) return null;
  return (
    <div className={styles.actions}>
      {actions.map((a) => (
        <span key={a} className={styles.action}>{a}</span>
      ))}
    </div>
  );
}

/** Renders a short scripted example as a mini mockup of the interface type. */
export function ExampleScene({ turns, interfaceType }: ExampleSceneProps) {
  const kind = interfaceType === 'Voice UI' ? 'voice' : interfaceType === 'Agentic UI' ? 'agentic' : 'chat';

  return (
    <div className={`${styles.frame} ${styles[kind]}`}>
      <div className={styles.frameBar}>
        <span className={styles.dots} aria-hidden="true"><i /><i /><i /></span>
        <span>{frameLabel[interfaceType]}</span>
      </div>
      <div className={styles.body}>
        {turns.map((t, i) => {
          if (t.role === 'system') {
            return (
              <div key={i} className={styles.system}>
                {kind === 'agentic' && <span className={styles.logDot} aria-hidden="true" />}
                {t.text}
              </div>
            );
          }

          if (kind === 'voice') {
            return (
              <div key={i} className={`${styles.voiceLine} ${styles[t.role]}`}>
                <span className={styles.speaker}>
                  {t.role === 'user' ? <MicIcon /> : <SpeakerIcon />}
                  {t.role === 'user' ? 'User' : 'Agent'}
                </span>
                <p>{t.text}</p>
                <Actions actions={t.actions} />
              </div>
            );
          }

          if (kind === 'agentic') {
            return t.role === 'agent' ? (
              <div key={i} className={styles.panel}>
                <p>{t.text}</p>
                <Actions actions={t.actions} />
              </div>
            ) : (
              <div key={i} className={styles.userAction}>
                <span aria-hidden="true">→</span> {t.text}
              </div>
            );
          }

          return (
            <div key={i} className={`${styles.bubbleRow} ${styles[t.role]}`}>
              <div className={styles.bubble}>{t.text}</div>
              {t.role === 'agent' && <Actions actions={t.actions} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
